// 11f (Parte H): os campos de anotação, observação e texto livre dos formulários usam o MESMO editor das páginas
// (blocos de texto com 🎤, checklist, lista, ✍️ caneta, ＋ Mais com tabela, link, imagem e arquivo) e, quando a
// pessoa abre, o quadro com a barra do editor (texto 11b). Um componente só, usado em todas as abas.
//
// Como guarda (compatível com as versões antigas do app):
//  - a chave original do campo (ex.: "notas") continua sendo TEXTO: é o espelho do que foi escrito. PDFs, relatórios,
//    compartilhar, busca e as versões antigas do app leem esse texto e continuam funcionando;
//  - o conteúdo completo (blocos e quadro) fica numa chave irmã (chaveRica: "notas__rico").
// Se uma versão antiga mudar só o texto, o espelho deixa de bater: vale o texto novo (imagens, arquivos e desenhos ficam).
import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import type { AbaId, BlocoPag, Quadro as Q, RicoCampo } from '../store/types';
import { PaginaEditor } from './PaginaEditor';
import { C, alpha } from './theme';
import { t } from '../lib/i18n';

export type { RicoCampo };

/** Chave irmã onde fica o conteúdo completo do campo. */
export const chaveRica = (k: string) => `${k}__rico`;

const ehRico = (x: unknown): x is RicoCampo => !!x && typeof x === 'object' && Array.isArray((x as RicoCampo).blocos);
const SO_ARQUIVO = new Set<BlocoPag['tipo']>(['imagem', 'arquivo', 'desenho']);

/** Texto dos blocos (o que tem letra: texto, listas, checklist, tabela e link). */
function textoDosBlocos(bs: BlocoPag[]): string {
  const partes: string[] = [];
  for (const b of bs) {
    switch (b.tipo) {
      case 'texto': if (b.texto.trim()) partes.push(b.texto.replace(/\s+$/, '')); break;
      case 'sub': if (b.texto.trim()) partes.push(b.texto.trim().toUpperCase()); break;   // texto 27 (A2)
      case 'check': case 'lista': {
        const l = b.itens.filter(i => i.text.trim()).map(i => (b.tipo === 'lista' ? '• ' : i.done ? '☑ ' : '☐ ') + i.text.trim());
        if (l.length) partes.push(l.join('\n'));
        break;
      }
      case 'tabela': {
        const l = b.linhas.filter(r => r.some(c => c.trim())).map(r => r.map(c => c.trim()).join(' | '));
        if (l.length) partes.push(l.join('\n'));
        break;
      }
      case 'link': if (b.url.trim() || b.titulo.trim()) partes.push([b.titulo.trim(), b.url.trim()].filter(Boolean).join(': ')); break;
    }
  }
  return partes.join('\n\n');
}

/** Texto escrito no quadro (textos e checklists soltos). */
function textoDoQuadro(q?: Q | null): string {
  if (!q) return '';
  const l = [...q.textos.map(x => x.texto.trim()).filter(Boolean),
    ...q.checks.flatMap(c => c.itens).filter(i => i.text.trim()).map(i => (i.done ? '☑ ' : '☐ ') + i.text.trim())];
  return l.join('\n');
}

/** Texto simples (espelho) do conteúdo completo: vai na chave original do campo. */
export function textoDoRico(r?: RicoCampo | null): string {
  if (!r) return '';
  return [textoDosBlocos(r.blocos), textoDoQuadro(r.quadro)].filter(Boolean).join('\n\n');
}

const temQuadro = (q?: Q | null) => !!q && (q.tracos.length > 0 || q.textos.some(x => x.texto.trim()) || q.checks.some(c => c.itens.some(i => i.text.trim()))
  || !!q.imagens?.length || !!q.tabelas?.length);

/** Texto 21 (A4): texto antigo com linhas "☐ / ☑ / •" vira blocos (texto, checklist, lista), sem perder nada.
 *  Os ids são fixos (pela posição) para o campo não perder o foco enquanto o editor ainda não foi salvo. */
export function blocosDoTexto(txt: string): BlocoPag[] {
  const out: BlocoPag[] = [];
  let atual: BlocoPag | null = null;
  txt.split('\n').forEach((ln, i) => {
    const m = /^(☐|☑|•)(?: |$)/.exec(ln);
    const tipo: BlocoPag['tipo'] = m ? (m[1] === '•' ? 'lista' : 'check') : 'texto';
    if (tipo === 'texto') {
      if (atual?.tipo === 'texto') { atual.texto += '\n' + ln; return; }
      if (!ln.trim() && atual) return;   // linha em branco entre blocos
      atual = { id: `tx${i}`, tipo: 'texto', texto: ln }; out.push(atual); return;
    }
    const item = { id: `it${i}`, text: ln.slice(m![0].length), done: m![1] === '☑' };
    if (atual && atual.tipo === tipo) { (atual as any).itens.push(item); return; }
    atual = { id: `bl${i}`, tipo, itens: [item] } as BlocoPag; out.push(atual);
  });
  for (const b of out) if (b.tipo === 'texto') b.texto = b.texto.replace(/\s+$/, '');
  return out.length ? out : [{ id: 'txt0', tipo: 'texto', texto: '' }];
}

/** Conteúdo para o editor: o completo, se ainda bate com o texto; senão, o texto (antigo ou mudado por versão antiga). */
export function ricoDoValor(valor: unknown, rico: unknown, converter = false): RicoCampo {
  const plano = typeof valor === 'string' ? valor : valor == null ? '' : String(valor);
  if (ehRico(rico) && textoDoRico(rico) === plano) return rico;
  if (converter && !ehRico(rico)) return { v: 1, blocos: blocosDoTexto(plano), quadro: null };
  const base = ehRico(rico) ? rico : null;
  let txt = plano;
  const tq = textoDoQuadro(base?.quadro);
  if (tq && txt.endsWith(tq)) txt = txt.slice(0, -tq.length).replace(/\s+$/, '');
  // id fixo: o bloco não muda de chave a cada desenho da tela (o campo não perde o foco)
  return { v: 1, blocos: [{ id: 'txt0', tipo: 'texto', texto: txt }, ...(base?.blocos.filter(b => SO_ARQUIVO.has(b.tipo)) ?? [])], quadro: base?.quadro ?? null };
}

/**
 * Campo de texto livre com o editor das páginas.
 * valor: o texto guardado na chave original; rico: o que está na chaveRica; onChange recebe os dois para guardar juntos.
 */
export function CampoRico({ valor, rico, onChange, cor, aba, dica, nome, converter, soTexto }: { valor: unknown; rico: unknown; onChange: (texto: string, rico: RicoCampo) => void;
  cor: string; aba?: AbaId; dica?: string; nome?: string;
  /** texto 21 (A4): o texto antigo com checklist/lista vira blocos do mesmo tipo */
  converter?: boolean;
  /** só Texto, Checklist e Lista (o campo é guardado só como texto, ex.: a pauta que vai para a equipe) */
  soTexto?: boolean }) {
  const atual = ricoDoValor(valor, rico, converter);
  const [barra, setBarra] = useState(false);
  const comQuadro = barra || temQuadro(atual.quadro);
  const mudar = (n: RicoCampo) => onChange(textoDoRico(n), n);
  return (
    <View style={{ gap: 8 }}>
      {!!dica && !textoDoRico(atual) && <Text style={{ color: C.mute, fontSize: 12 }}>{dica}</Text>}
      {!soTexto && !temQuadro(atual.quadro) && (
        <Pressable onPress={() => setBarra(x => !x)} hitSlop={4} accessibilityLabel={barra ? t('Fechar a barra do editor') : t('Abrir a barra do editor')}
          style={{ alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 8, minHeight: 36, borderRadius: 14,
            backgroundColor: barra ? alpha(cor, 0.25) : C.card2 }}>
          <Text style={{ color: C.text, fontSize: 13, fontWeight: '600' }}>🛠️ {barra ? t('Fechar a barra do editor') : t('Barra do editor (caneta, marca-texto, texto)')}</Text>
        </Pressable>
      )}
      <PaginaEditor blocos={atual.blocos} cor={cor} aba={aba} nome={nome} soTexto={soTexto}
        onChange={bs => mudar({ ...atual, blocos: bs })}
        quadro={atual.quadro} onQuadro={comQuadro && !soTexto ? q => mudar({ ...atual, quadro: q }) : undefined} />
    </View>
  );
}
